// Minimal Matroska/WebM (EBML) audio demuxer. mp4box handles ISO-BMFF (MP4/MOV)
// but not Matroska, so this extracts each audio track's encoded frames from
// .mkv/.webm and decodes them with WebCodecs (Opus + AAC; other codecs fall back
// to a single decodeAudioData mix in the caller).
//
// It parses the file as a STREAM (file.stream()) so memory stays bounded even for
// large files: big master elements (Segment/Cluster) are walked child-by-child
// without buffering them whole, and video blocks are skipped without being read
// into memory — only the audio track's frames are retained.
import { assembleAudioBuffer } from "./demux-audio";

// --- EBML element IDs (full, including marker bits) ---
const ID = {
  Segment: 0x18538067, Info: 0x1549a966, TimestampScale: 0x2ad7b1,
  Tracks: 0x1654ae6b, TrackEntry: 0xae, TrackNumber: 0xd7, TrackType: 0x83,
  CodecID: 0x86, CodecPrivate: 0x63a2, Audio: 0xe1, SamplingFrequency: 0xb5, Channels: 0x9f,
  Cluster: 0x1f43b675, Timestamp: 0xe7, SimpleBlock: 0xa3, BlockGroup: 0xa0, Block: 0xa1,
  Position: 0xa7, PrevSize: 0xab, SilentTracks: 0x5854, EncryptedBlock: 0xaf,
};
// Elements that may appear directly inside a Cluster (used to detect where an
// unknown-size cluster ends, since the next non-child element belongs to Segment).
const CLUSTER_CHILDREN = new Set<number>([ID.Timestamp, ID.SimpleBlock, ID.BlockGroup, ID.Position, ID.PrevSize, ID.SilentTracks, ID.EncryptedBlock]);

interface MkvTrack {
  number: number; codecId: string; codecPrivate?: Uint8Array;
  sampleRate: number; channels: number; frames: { data: Uint8Array; ts: number }[];
}
interface Vint { value: number; length: number; unknown: boolean }
interface Header { id: number; size: Vint }

// --- small helpers for parsing in-memory (small) element bodies ---
function readVint(b: Uint8Array, pos: number, keepMarker: boolean): Vint {
  const first = b[pos];
  if (first === undefined) return { value: 0, length: 1, unknown: false };
  let mask = 0x80, len = 1;
  while (len <= 8 && !(first & mask)) { mask >>= 1; len++; }
  if (len > 8) return { value: 0, length: 1, unknown: false };
  let value = keepMarker ? first : first & (mask - 1);
  let allOnes = (first & (mask - 1)) === (mask - 1);
  for (let i = 1; i < len; i++) { const byte = b[pos + i]; if (byte !== 0xff) allOnes = false; value = value * 256 + byte; }
  return { value, length: len, unknown: !keepMarker && allOnes };
}
const u = (b: Uint8Array, s = 0, e = b.length) => { let v = 0; for (let i = s; i < e; i++) v = v * 256 + b[i]; return v; };
const f = (b: Uint8Array, s: number, e: number) => {
  const dv = new DataView(b.buffer, b.byteOffset + s, e - s);
  return e - s === 4 ? dv.getFloat32(0) : e - s === 8 ? dv.getFloat64(0) : 0;
};
const str = (b: Uint8Array, s: number, e: number) => { let r = ""; for (let i = s; i < e; i++) if (b[i]) r += String.fromCharCode(b[i]); return r; };

/** A forward-only byte stream over a File with a small pending buffer + 1 pushback. */
class MkvStream {
  private reader: ReadableStreamDefaultReader<Uint8Array>;
  private buf = new Uint8Array(0);
  private off = 0;
  private eof = false;
  pos = 0;                 // absolute file position of buf[off]
  private pending: Header | null = null;

  constructor(stream: ReadableStream<Uint8Array>) { this.reader = stream.getReader(); }

  private async pull(): Promise<boolean> {
    if (this.eof) return false;
    const { value, done } = await this.reader.read();
    if (done || !value) { this.eof = true; return false; }
    const keep = this.off > 0 ? this.buf.subarray(this.off) : this.buf;
    const merged = new Uint8Array(keep.length + value.length);
    merged.set(keep); merged.set(value, keep.length);
    this.buf = merged; this.off = 0;
    return true;
  }
  private async fill(n: number): Promise<boolean> {
    while (this.buf.length - this.off < n) { if (!(await this.pull())) return false; }
    return true;
  }
  async hasMore(): Promise<boolean> { return !!this.pending || await this.fill(1); }

  async take(n: number): Promise<Uint8Array> {
    await this.fill(n);
    const avail = Math.min(n, this.buf.length - this.off);
    const out = this.buf.slice(this.off, this.off + avail);
    this.off += avail; this.pos += avail;
    return out;
  }
  async skip(n: number): Promise<void> {
    let rem = n;
    while (rem > 0) {
      const have = this.buf.length - this.off;
      if (have > 0) { const c = Math.min(have, rem); this.off += c; this.pos += c; rem -= c; }
      else if (!(await this.pull())) break;
    }
  }
  private async readVint(keepMarker: boolean): Promise<Vint> {
    if (!(await this.fill(1))) return { value: 0, length: 1, unknown: false };
    const first = this.buf[this.off];
    let len = 1, mask = 0x80;
    while (len <= 8 && !(first & mask)) { mask >>= 1; len++; }
    await this.fill(len);
    const v = readVint(this.buf, this.off, keepMarker);
    this.off += v.length; this.pos += v.length;
    return v;
  }
  async readHeader(): Promise<Header> {
    if (this.pending) { const p = this.pending; this.pending = null; return p; }
    const id = await this.readVint(true);
    const size = await this.readVint(false);
    return { id: id.value, size };
  }
  pushBack(h: Header) { this.pending = h; }
  /** Peek the leading track-number vint of a block body without consuming it. */
  async peekTrack(): Promise<number> { await this.fill(2); return readVint(this.buf, this.off, false).value; }
}

/** Demux + WebCodecs-decode every audio track of an MKV/WebM file. */
export async function demuxMatroskaAudio(file: File, ctx: BaseAudioContext, diag?: { codecs: string[] }): Promise<AudioBuffer[]> {
  const s = new MkvStream(file.stream());
  const tracks = new Map<number, MkvTrack>();
  let timestampScale = 1_000_000; // ns per tick (default)
  let clusterTs = 0;

  // Walk the top level to the Segment.
  let seg: Header | null = null;
  while (await s.hasMore()) {
    const h = await s.readHeader();
    if (h.id === ID.Segment) { seg = h; break; }
    if (h.size.unknown) return []; // only Segment/Cluster use unknown size
    await s.skip(h.size.value);
  }
  if (!seg) return [];
  const segEnd = seg.size.unknown ? Infinity : s.pos + seg.size.value;

  while (s.pos < segEnd && await s.hasMore()) {
    const h = await s.readHeader();
    if (h.id === ID.Info) { timestampScale = timestampScaleOf(await s.take(h.size.value)) || timestampScale; }
    else if (h.id === ID.Tracks) { parseTracks(await s.take(h.size.value), tracks); }
    else if (h.id === ID.Cluster) { clusterTs = await parseCluster(s, h, clusterTs, tracks); }
    else if (h.size.unknown) break; // unexpected unknown-size element → stop
    else await s.skip(h.size.value);
  }

  const allAudio = [...tracks.values()].filter((t) => t.codecId.startsWith("A_"));
  if (diag) diag.codecs = allAudio.map((t) => t.codecId.replace(/^A_/, ""));
  const out: AudioBuffer[] = [];
  for (const t of allAudio) { if (!t.frames.length) continue; const buf = await decodeTrack(t, timestampScale, ctx); if (buf) out.push(buf); }
  return out;
}

async function parseCluster(s: MkvStream, cluster: Header, clusterTs: number, tracks: Map<number, MkvTrack>): Promise<number> {
  const known = !cluster.size.unknown;
  const end = known ? s.pos + cluster.size.value : Infinity;
  while (s.pos < end && await s.hasMore()) {
    const h = await s.readHeader();
    if (!known && !CLUSTER_CHILDREN.has(h.id)) { s.pushBack(h); break; } // cluster ended
    if (h.id === ID.Timestamp) { clusterTs = u(await s.take(h.size.value)); }
    else if (h.id === ID.SimpleBlock) { await handleBlock(s, h.size.value, clusterTs, tracks); }
    else if (h.id === ID.BlockGroup) { await parseBlockGroup(s, h, clusterTs, tracks); }
    else if (h.size.unknown) break;
    else await s.skip(h.size.value);
  }
  return clusterTs;
}

async function parseBlockGroup(s: MkvStream, group: Header, clusterTs: number, tracks: Map<number, MkvTrack>): Promise<void> {
  const end = group.size.unknown ? Infinity : s.pos + group.size.value;
  while (s.pos < end && await s.hasMore()) {
    const h = await s.readHeader();
    if (h.id === ID.Block) await handleBlock(s, h.size.value, clusterTs, tracks);
    else if (h.size.unknown) break;
    else await s.skip(h.size.value);
  }
}

/** Read a block only if it belongs to an audio track; otherwise skip it (so video
 *  frames are never buffered into memory). */
async function handleBlock(s: MkvStream, len: number, clusterTs: number, tracks: Map<number, MkvTrack>): Promise<void> {
  const trackNum = await s.peekTrack();
  if (tracks.has(trackNum)) { const body = await s.take(len); collectBlock(body, clusterTs, tracks); }
  else await s.skip(len);
}

function timestampScaleOf(body: Uint8Array): number {
  let pos = 0;
  while (pos < body.length) {
    const id = readVint(body, pos, true); pos += id.length;
    const size = readVint(body, pos, false); pos += size.length;
    const e = Math.min(body.length, pos + size.value);
    if (id.value === ID.TimestampScale) return u(body, pos, e);
    pos = e;
  }
  return 0;
}

function parseTracks(body: Uint8Array, tracks: Map<number, MkvTrack>): void {
  let pos = 0;
  while (pos < body.length) {
    const id = readVint(body, pos, true); pos += id.length;
    const size = readVint(body, pos, false); pos += size.length;
    const e = Math.min(body.length, pos + size.value);
    if (id.value === ID.TrackEntry) { const t = parseTrackEntry(body, pos, e); if (t) tracks.set(t.number, t); }
    pos = e;
  }
}

function parseTrackEntry(b: Uint8Array, start: number, end: number): MkvTrack | null {
  const t: MkvTrack = { number: 0, codecId: "", sampleRate: 48000, channels: 2, frames: [] };
  let type = 0, pos = start;
  while (pos < end) {
    const id = readVint(b, pos, true); pos += id.length;
    const size = readVint(b, pos, false); pos += size.length;
    const body = pos, bodyEnd = Math.min(end, pos + size.value);
    if (id.value === ID.TrackNumber) t.number = u(b, body, bodyEnd);
    else if (id.value === ID.TrackType) type = u(b, body, bodyEnd);
    else if (id.value === ID.CodecID) t.codecId = str(b, body, bodyEnd);
    else if (id.value === ID.CodecPrivate) t.codecPrivate = b.slice(body, bodyEnd);
    else if (id.value === ID.Audio) {
      let p = body;
      while (p < bodyEnd) {
        const aid = readVint(b, p, true); p += aid.length;
        const asz = readVint(b, p, false); p += asz.length;
        const ab = p, ae = Math.min(bodyEnd, p + asz.value);
        if (aid.value === ID.SamplingFrequency) t.sampleRate = Math.round(f(b, ab, ae)) || t.sampleRate;
        else if (aid.value === ID.Channels) t.channels = u(b, ab, ae) || t.channels;
        p = ae;
      }
    }
    pos = bodyEnd;
  }
  return type === 2 ? t : null; // 2 = audio
}

/** Parse a (Simple)Block body: track vint, int16 rel-ts, flags, then frame(s). */
function collectBlock(b: Uint8Array, clusterTs: number, tracks: Map<number, MkvTrack>): void {
  const end = b.length;
  let pos = 0;
  const tn = readVint(b, pos, false); pos += tn.length;
  const track = tracks.get(tn.value);
  const rel = (b[pos] << 8) | b[pos + 1]; pos += 2;
  const relTs = rel > 0x7fff ? rel - 0x10000 : rel;
  const flags = b[pos]; pos += 1;
  const lacing = (flags >> 1) & 0x03;
  const ts = clusterTs + relTs;
  if (!track) return;

  if (lacing === 0) { track.frames.push({ data: b.slice(pos, end), ts }); return; }
  const count = b[pos] + 1; pos += 1;
  const sizes: number[] = [];
  if (lacing === 2) { // Xiph
    for (let i = 0; i < count - 1; i++) { let sz = 0; while (b[pos] === 0xff) { sz += 255; pos++; } sz += b[pos++]; sizes.push(sz); }
  } else if (lacing === 1) { // fixed
    const each = Math.floor((end - pos) / count);
    for (let i = 0; i < count - 1; i++) sizes.push(each);
  } else { // EBML lacing
    const first = readVint(b, pos, false); pos += first.length; sizes.push(first.value);
    let prev = first.value;
    for (let i = 1; i < count - 1; i++) {
      const d = readVint(b, pos, false); pos += d.length;
      const half = (1 << (7 * d.length - 1)) - 1; // signed bias
      prev += d.value - half; sizes.push(prev);
    }
  }
  for (let i = 0; i < count; i++) {
    const sz = i < sizes.length ? sizes[i] : end - pos;
    track.frames.push({ data: b.slice(pos, pos + sz), ts });
    pos += sz;
  }
}

async function decodeTrack(t: MkvTrack, timestampScale: number, ctx: BaseAudioContext): Promise<AudioBuffer | null> {
  if (typeof AudioDecoder === "undefined") return null;
  const cfg = audioConfig(t);
  if (!cfg) return null; // unsupported codec → caller falls back

  const chunks: { data: Float32Array[]; frames: number }[] = [];
  let outCh = cfg.numberOfChannels, outRate = cfg.sampleRate, failed = false;
  const dec = new AudioDecoder({
    output: (ad) => {
      outCh = ad.numberOfChannels; outRate = ad.sampleRate;
      const frames = ad.numberOfFrames, data: Float32Array[] = [];
      for (let c = 0; c < outCh; c++) { const arr = new Float32Array(frames); ad.copyTo(arr, { planeIndex: c, format: "f32-planar" }); data.push(arr); }
      chunks.push({ data, frames }); ad.close();
    },
    error: (e) => { failed = true; console.warn("[webcut] mkv AudioDecoder error", e); },
  });
  try {
    dec.configure(cfg);
    for (const fr of t.frames) {
      dec.decode(new EncodedAudioChunk({ type: "key", timestamp: Math.round((fr.ts * timestampScale) / 1000), data: fr.data }));
    }
    await dec.flush();
  } catch (e) { console.warn("[webcut] mkv track decode failed", e); try { dec.close(); } catch {} return null; }
  try { dec.close(); } catch {}
  if (failed) return null;
  return assembleAudioBuffer(ctx, chunks, outCh, outRate);
}

function audioConfig(t: MkvTrack): AudioDecoderConfig | null {
  if (t.codecId === "A_OPUS") {
    return { codec: "opus", sampleRate: 48000, numberOfChannels: t.channels, ...(t.codecPrivate ? { description: t.codecPrivate } : {}) };
  }
  if (t.codecId === "A_AAC") {
    // CodecPrivate is the AudioSpecificConfig; object type lives in its first 5 bits.
    const aot = t.codecPrivate ? t.codecPrivate[0] >> 3 : 2;
    return { codec: `mp4a.40.${aot || 2}`, sampleRate: t.sampleRate, numberOfChannels: t.channels, ...(t.codecPrivate ? { description: t.codecPrivate } : {}) };
  }
  return null; // Vorbis / AC-3 / MP3 etc. → fall back to decodeAudioData
}
